/* Copyright (c) 2021 Caring Transitions Online Auctions. All Rights Reserved. */

// library imports
import { Component, createRef } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';

// Projects import
import { ToasterMessage } from 'shared/toast/toast';
import Validators from 'shared/validators';


// style imports
import './AdminLogin.scss';
import { AdminService } from 'shared/services';
import { ACCESS_TOKEN, REFRESH_TOKEN } from 'shared/settings/token';
import { loginWithRes } from 'shared/settings/auth';

/**
 * make authentic user to enter the system
 * 
**/

export class AdminLogin extends Component {

    constructor(props) {
        super(props);
        this.toasterMessage = createRef();

        this.state = {
            // toggle input type
            showPassword: false,

            // form
            formValid: false,
            formControls: {
                buyeremail: { value: '', valid: false, error: '', validators: [Validators.required(), Validators.email(), Validators.maxLength(60)] },
                secretkey: { value: '', valid: false, error: '', validators: [Validators.required()] },
                email: { value: '', valid: false, error: '', validators: [Validators.required(), Validators.email(), Validators.maxLength(60)] },
                password: { value: '', valid: false, error: '', validators: [Validators.required()] }
            },
            remember: false,

            // loading
            signinFormLoading: false,

            //status
            signinStatus: { show: false, type: '', message: '' }
        };
    }

    componentDidMount() {
        this.removeLoader();
    }

    removeLoader = () => {
        const loader = document.querySelector('.pre-loader');
        const root = document.querySelector('.ct-bids');
        // console.log(loader, root);
        loader.classList.add('hide-loader');
        root.classList.add('loaded');
    }

    /* toggles password to show or hide on clicking eye icon */
    togglePassword = () => {
        this.setState({ showPassword: !this.state.showPassword });
    };

    /* checks whether the input is valid or not */
    onInputValidation = (event) => {
        let controlError = '';

        /* checks all the validators for the control */
        for (let validator of this.state.formControls[event.target.name].validators) {
            controlError = validator(event.target.value);
            if (controlError) break;
        }

        /* sets the state of the control and form validity */
        this.setState({
            formControls: {
                ...this.state.formControls,
                [event.target.name]: {
                    ...this.state.formControls[event.target.name],
                    value: event.target.value,
                    valid: !Boolean(controlError),
                    error: controlError
                }
            }
        },
            () => {
                /* check form validity */
                if (controlError)
                    this.setState({ formValid: false });
                else {
                    let formValid = true;
                    for (let control of Object.keys(this.state.formControls)) {
                        if (!this.state.formControls[control].valid) {
                            formValid = false;
                            break;
                        }
                    }
                    this.setState({ formValid: formValid });
                }
            });
    }

    /* get longer token validity */
    keepMeSignin = (event) => {
        this.setState({ remember: event.target.checked });
    };

    /* called when signin button is clicked */
    onSignin = async (event) => {
        event.preventDefault();
        this.toasterMessage.current.hideMessage(0);
        if (this.state.formValid) {
            const data = {
                data: {
                    username: this.state.formControls.email.value,
                    password: btoa(this.state.formControls.password.value),
                    'keep-user-logged-in': this.state.remember
                }
            };
            this.setState({ signinFormLoading: true });

            /* api call to get user token */
            AdminService.logIn(data).then(
                response => {
                    const jsonData = response.data;
                    AdminService.getBuyerToken(jsonData.accessToken).then(
                        buyerToken => {
                            const result = buyerToken.data;
                            const externalAuth = {
                                "username": this.state.formControls.buyeremail.value,
                                "secretkey": this.state.formControls.secretkey.value
                            };
                            AdminService.getToken({ data: externalAuth }, result.accessToken).then(
                                res => {
                                    const jsonLoginData = res.data;
                                    loginWithRes({ [ACCESS_TOKEN]: jsonLoginData['accessToken'], [REFRESH_TOKEN]: jsonLoginData['refreshToken'] }, {props: this.props}).then(
                                        afterLogin => {
                                            this.setState({ signinFormLoading: false });
                                            this.props.history.push('/');
                                        }
                                    ).catch(
                                        error => {
                                            this.setState({ signinFormLoading: false });
                                        }
                                    );
                                }
                            ).catch(error => {
                                this.setState({
                                    signinFormLoading: false,
                                    signinStatus: { show: true, type: 'danger', message: "Buyer email does not exist. Please try again." }
                                }, () => {
                                    this.toasterMessage.current.showMessage({ type: this.state.signinStatus.type, message: this.state.signinStatus.message }, 3000, true);
                                });
                            });
                        }
                    ).catch(error => {
                        this.setState({
                            signinFormLoading: false,
                            signinStatus: { show: true, type: 'danger', message: "Sorry, we couldn't find an account with that email and password combination. Please try again." }
                        }, () => {
                            this.toasterMessage.current.showMessage({ type: this.state.signinStatus.type, message: this.state.signinStatus.message }, 3000, true);
                        });
                    });
                }
            ).catch(error => {
                if (error.message.search('Unauthorized') !== -1) {
                    this.setState({
                        signinFormLoading: false,
                        signinStatus: { show: true, type: 'danger', message: "Sorry, we couldn't find an account with that email and password combination. Please try again." }
                    }, () => {
                        this.toasterMessage.current.showMessage({ type: this.state.signinStatus.type, message: this.state.signinStatus.message }, 3000, true);
                    });
                }
                else
                    this.setState({
                        signinFormLoading: false,
                        signinStatus: { show: true, type: 'danger', message: error.response.data['message'] || 'Failed to reach server' }
                    }, () => {
                        this.toasterMessage.current.showMessage({ type: this.state.signinStatus.type, message: this.state.signinStatus.message }, 3000, true);
                    });
            });
        }
    }

    render() {
        return (
            <div className="d-flex align-items-center justify-content-center admin-signin">
                <div className="d-flex flex-column signin-card px-3 px-md-4 px-lg-5">
                    <div className="d-flex justify-content-center my-4">
                        <img src={`${process.env.PUBLIC_URL}/static/logo/Admin_logo.svg`} alt="logo" height="50px" width="170px" />
                    </div>
                    <div className="my-4">
                        <span className="title"><b>Sign In</b></span>
                        <Form className="d-flex flex-column justify-content-center my-3 px-2" onSubmit={(event) => this.onSignin(event)}>

                            <div className="float-control">
                                <div className="text">
                                    {
                                        this.state.formControls.email.valid ? null : <small className="error">{this.state.formControls.email.error}</small>
                                    }
                                </div>
                                <Form.Control autoFocus type="email" id="signin-email" name="email" value={this.state.formControls.email.value} placeholder=" " onChange={(event) => this.onInputValidation(event)} />
                                <label htmlFor="signin-email">Email</label>
                            </div>

                            <div className="float-control">
                                {
                                    this.state.showPassword ?
                                        <i className="password-icon fa fa-eye fa-flip-horizontal" onClick={this.togglePassword} /> :
                                        <i className="password-icon fa fa-eye-slash fa-flip-horizontal" onClick={this.togglePassword} />
                                }
                                <div className="text">
                                    {
                                        this.state.formControls.password.valid ? null : <small className="error">{this.state.formControls.password.error}</small>
                                    }
                                </div>
                                <Form.Control type={this.state.showPassword ? 'text' : 'password'} id="signin-password" name="password" value={this.state.formControls.password.value} placeholder=" " onChange={(event) => this.onInputValidation(event)} />
                                <label htmlFor="signin-password">Password</label>
                            </div>

                            <div className="float-control">
                                <div className="text">
                                    {
                                        this.state.formControls.secretkey.valid ? null : <small className="error">{this.state.formControls.secretkey.error}</small>
                                    }
                                </div>
                                <Form.Control id="secretkey" name="secretkey" value={this.state.formControls.secretkey.value} placeholder=" " onChange={(event) => this.onInputValidation(event)} />
                                <label htmlFor="secretkey">Secret Key</label>
                            </div>

                            <div className="float-control">
                                <div className="text">
                                    {
                                        this.state.formControls.buyeremail.valid ? null : <small className="error">{this.state.formControls.buyeremail.error}</small>
                                    }
                                </div>
                                <Form.Control type="email" id="signin-buyeremail" name="buyeremail" value={this.state.formControls.buyeremail.value} placeholder=" " onChange={(event) => this.onInputValidation(event)} />
                                <label htmlFor="signin-buyeremail">Buyer Email</label>
                            </div>

                            <Form.Check className="my-3" type="checkbox" id="signin-checkbox" label="Keep me signed in" value={this.state.remember} onChange={this.keepMeSignin} />

                            <div>
                                <div className="d-flex flex-row">
                                    <ToasterMessage ref={this.toasterMessage} className="alert-handle mt-2"></ToasterMessage>
                                </div>
                                <div className="d-flex flex-row justify-content-between my-2">
                                    <div className="d-flex flex-row align-items-center">
                                        <Button variant="secondary" style={{ cursor: this.state.formValid ? 'pointer' : 'not-allowed' }} type="submit"
                                            disabled={!this.state.formValid || this.state.signinFormLoading || this.state.signinStatus.type === 'success'}>Sign In</Button>
                                        {
                                            this.state.signinFormLoading ?
                                                <Spinner animation="border" variant="info" style={{ margin: '0 8px' }} /> :
                                                null
                                        }
                                    </div>
                                    {/* <div className="d-flex align-items-center">
                                        <Link to="/forgot-password">
                                            <small className="text-secondary" style={{ fontSize: "14px" }}>Forgot Password?</small>
                                        </Link>
                                    </div> */}
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
        );
    }
}